import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import globalConfig from '../../../../../configs/globalConfigs';
import Cookies from 'js-cookie';
import moment from 'moment';
import { IconButton, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import api from '../../../../../services/api';

import icone_pgm from '../../../../../assets/imgs/icone_pgm.png';
import icone_medidor from '../../../../../assets/imgs/icone_medidor.png';
import icone_subestacao from '../../../../../assets/imgs/icone_subestacao.png';
import icone_casa from '../../../../../assets/imgs/icone_casa.png';
import icone_loja from '../../../../../assets/imgs/icone_loja.png';
import alarmeAudio from '../../../../../assets/mp3/alarme.mp3';

// Fix for Leaflet default icons
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// Audio Manager (Singleton)
const audioManager = {
    audio: new Audio(alarmeAudio),
    isPlaying: false,
    play() {
        if (!this.isPlaying) {
            this.audio.loop = true;
            this.audio.volume = 1.0;
            this.audio
                .play()
                .then(() => {
                    this.isPlaying = true;
                })
                .catch(error => console.error("Erro ao reproduzir o áudio:", error));
        }
    },
    pause() {
        if (this.isPlaying) {
            this.audio.pause();
            this.audio.currentTime = 0;
            this.isPlaying = false;
        }
    }
};

// Permanent Satellite Tiles
const satelliteTiles = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

const MapaDoLocal = React.memo((props) => {
    const { instalacao: propInstalacao } = props;
    const [instalacao, setInstalacao] = useState(propInstalacao);
    
    const [darkMode, setDarkMode] = useState(() => {
        const saved = Cookies.get('darkMode') || Cookies.get('tema');
        return saved === 'true' || saved === 'dark';
    });

    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const tileLayerRef = useRef(null);
    const markersLayerRef = useRef(L.layerGroup());

    // Helper functions
    const processarIconeInstalacao = useCallback((tipo) => {
        if (tipo === 0) return icone_casa;
        if (tipo === 1) return icone_loja;
        return icone_subestacao;
    }, []);

    // Update markers and layers when dependencies change
    const updateMapLayers = useCallback(() => {
        if (!mapInstanceRef.current || !instalacao) return;

        markersLayerRef.current.clearLayers();

        // 1. Render Installation Marker
        const lat = parseFloat(instalacao.latitude);
        const lng = parseFloat(instalacao.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
            const iconPath = processarIconeInstalacao(instalacao.icone_mapa);
            const customIcon = L.divIcon({
                className: 'custom-div-icon',
                html: `
                    <div style="display: flex; flex-direction: column; align-items: center; transform: translate(-50%, -100%);">
                        <img src="${iconPath}" style="width: 40px; height: 40px;" />
                        <span style="color: white; font-size: 10px; text-shadow: 1px 1px 0px black, -1px -1px 0px black; font-weight: bold; text-align: center; white-space: nowrap;">
                            ${instalacao.nome_mapa}
                        </span>
                    </div>
                `,
                iconSize: [40, 40],
                iconAnchor: [20, 40]
            });

            L.marker([lat, lng], { icon: customIcon }).addTo(markersLayerRef.current);
        }

        // 2. Render Equipment Markers
        if (instalacao.equipamentos) {
            instalacao.equipamentos.forEach(equip => {
                const eLat = parseFloat(equip.latitude);
                const eLng = parseFloat(equip.longitude);
                if (isNaN(eLat) || isNaN(eLng)) return;

                if (equip.tipo_equipamento_area_tecnica === 0 && equip.modulo_pgm) {
                    const alarme = equip.alarme;
                    const statusColor = alarme ? '#ef4444' : '#10b981';
                    const pgmIcon = L.divIcon({
                        className: 'pgm-icon',
                        html: `
                            <div style="display: flex; flex-direction: column; align-items: center; transform: translate(-50%, -100%);">
                                <div style="width: 32px; height: 32px; background-color: ${statusColor}; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 4px 6px rgba(0,0,0,0.3);">
                                    <img src="${icone_pgm}" style="width: 20px; height: 20px; filter: brightness(0) invert(1);" />
                                </div>
                                <span style="color: white; font-size: 10px; text-shadow: 1px 1px 2px black; font-weight: bold; margin-top: 2px;">${equip.nome_mapa}</span>
                            </div>
                        `,
                        iconSize: [32, 32],
                        iconAnchor: [16, 32]
                    });
                    L.marker([eLat, eLng], { icon: pgmIcon }).addTo(markersLayerRef.current);
                }
            });
        }

    }, [instalacao, processarIconeInstalacao]);

    // Initial Map Setup
    useEffect(() => {
        if (!mapContainerRef.current) return;

        const initialLat = parseFloat(propInstalacao?.latitude) || -15.7942;
        const initialLng = parseFloat(propInstalacao?.longitude) || -47.8822;

        const map = L.map(mapContainerRef.current, {
            center: [initialLat, initialLng],
            zoom: 16,
            zoomControl: true,
            attributionControl: false
        });

        tileLayerRef.current = L.tileLayer(satelliteTiles).addTo(map);
        markersLayerRef.current.addTo(map);

        mapInstanceRef.current = map;

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, []); // Run only on mount

    // Update installation data and map center
    useEffect(() => {
        setInstalacao(propInstalacao);
        if (mapInstanceRef.current && propInstalacao) {
            const lat = parseFloat(propInstalacao.latitude);
            const lng = parseFloat(propInstalacao.longitude);
            if (!isNaN(lat) && !isNaN(lng)) {
                // Fly to new location for a premium feel
                mapInstanceRef.current.flyTo([lat, lng], 16, {
                    duration: 1.5,
                    easeLinearity: 0.25
                });
            }
        }
    }, [propInstalacao.id_instalacao, propInstalacao.latitude, propInstalacao.longitude]);

    // Trigger marker updates when data changes
    useEffect(() => {
        updateMapLayers();
    }, [updateMapLayers]);

    // Audio Logic
    useEffect(() => {
        const hasAlarme = instalacao?.equipamentos?.some(e => e.alarme);
        if (hasAlarme) {
            audioManager.play();
        } else {
            audioManager.pause();
        }
        return () => audioManager.pause();
    }, [instalacao]);

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', borderRadius: '16px' }}>
            <div ref={mapContainerRef} style={{ width: '100%', height: '100%', minHeight: '300px' }} />
            
            <div style={{ 
                position: 'absolute', bottom: 16, left: 16, zIndex: 1000, 
                backgroundColor: 'rgba(15, 23, 42, 0.8)', padding: '8px 16px', 
                borderRadius: '8px', backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255,255,255,0.1)',
                display: 'flex', alignItems: 'center', gap: '8px'
            }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10b981', boxShadow: '0 0 8px #10b981' }} />
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    MODO SATÉLITE ATIVO
                </Typography>
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    // Memoization check: Only re-render if installation ID or telemetry alarms change
    // This keeps the map instance very stable
    if (prevProps.instalacao.id_instalacao !== nextProps.instalacao.id_instalacao) return false;
    
    // Check if any equipment alarm status changed
    const prevAlarms = prevProps.instalacao.equipamentos.map(e => e.alarme).join(',');
    const nextAlarms = nextProps.instalacao.equipamentos.map(e => e.alarme).join(',');
    
    return prevAlarms === nextAlarms;
});

export default MapaDoLocal;
